import { lazy, Suspense } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';

import { ProtectedRoute } from '@components/ProtectedRoute';
import { OptionalAuthRoute } from '@components/OptionalAuthRoute';
import { LoadingSpinner } from '@components/LoadingSpinner';
import { HomePage } from '@features/home';
import { LoginPage, SignupPage } from '@features/auth';
import { HubRequestPage } from '@features/partner';
import { BlogPage, BlogPostPage } from '@features/blog';
import { BookingPage, BookingConfirmPage, MyBookingsPage, ProfilePage, CartPage, CheckoutPage } from '@features/customer';
import { UserRole } from '@shared/types/user';
import { ROUTES } from '@lib/routes';

// Lazy load route modules for code splitting
const PublicCustomerRoutes = lazy(() => import('@features/customer/PublicCustomerRoutes'));

const PartnerPortal = lazy(() => import('@features/partner/PartnerPortal'));

const AdminDashboard = lazy(() => import('@features/admin/AdminDashboard'));

/**
 * Main application routes with code splitting and role-based access control
 * Each interface is lazy-loaded to reduce initial bundle size
 */
export function AppRoutes(): JSX.Element {
  return (
    <Suspense
      fallback={
        <div className="flex items-center justify-center min-h-screen">
          <LoadingSpinner size="lg" />
        </div>
      }
    >
      <Routes>
        {/* Public routes */}
        <Route path={ROUTES.HOME} element={<HomePage />} />
        <Route path={ROUTES.LOGIN} element={<LoginPage />} />
        <Route path={ROUTES.SIGNUP} element={<SignupPage />} />
        <Route path={ROUTES.FORGOT_PASSWORD} element={<div>Forgot Password</div>} />
        <Route path={ROUTES.BECOME_PARTNER} element={<HubRequestPage />} />
        <Route path={ROUTES.BLOG} element={<BlogPage />} />
        <Route path={ROUTES.BLOG_POST} element={<BlogPostPage />} />

        {/* Public Customer Routes - Store and Bike Search (no auth required) */}
        <Route
          path={`${ROUTES.CUSTOMER.BASE}/search`}
          element={
            <OptionalAuthRoute>
              <PublicCustomerRoutes />
            </OptionalAuthRoute>
          }
        />
        <Route
          path={`${ROUTES.CUSTOMER.BASE}/bikes/:bikeId`}
          element={
            <OptionalAuthRoute>
              <PublicCustomerRoutes />
            </OptionalAuthRoute>
          }
        />
        <Route
          path={`${ROUTES.CUSTOMER.BASE}/products`}
          element={
            <OptionalAuthRoute>
              <PublicCustomerRoutes />
            </OptionalAuthRoute>
          }
        />
        <Route
          path={`${ROUTES.CUSTOMER.BASE}/products/:productId`}
          element={
            <OptionalAuthRoute>
              <PublicCustomerRoutes />
            </OptionalAuthRoute>
          }
        />

        {/* Protected Customer Routes - Booking, Cart, Profile (auth required) */}
        <Route path={ROUTES.CUSTOMER.BOOKING} element={<ProtectedRoute allowedRoles={[UserRole.USER, UserRole.PARTNER, UserRole.ADMIN]}><BookingPage /></ProtectedRoute>} />
        <Route path={ROUTES.CUSTOMER.BOOKING_CONFIRM} element={<ProtectedRoute allowedRoles={[UserRole.USER, UserRole.PARTNER, UserRole.ADMIN]}><BookingConfirmPage /></ProtectedRoute>} />
        <Route path={ROUTES.CUSTOMER.MY_BOOKINGS} element={<ProtectedRoute allowedRoles={[UserRole.USER, UserRole.PARTNER, UserRole.ADMIN]}><MyBookingsPage /></ProtectedRoute>} />
        <Route path={ROUTES.CUSTOMER.PROFILE} element={<ProtectedRoute allowedRoles={[UserRole.USER, UserRole.PARTNER, UserRole.ADMIN]}><ProfilePage /></ProtectedRoute>} />
        <Route path={ROUTES.CUSTOMER.CART} element={<OptionalAuthRoute><CartPage /></OptionalAuthRoute>} />
        <Route path={ROUTES.CUSTOMER.CHECKOUT} element={<ProtectedRoute allowedRoles={[UserRole.USER, UserRole.PARTNER, UserRole.ADMIN]}><CheckoutPage /></ProtectedRoute>} />

        {/* Partner Portal - accessible to partners and admins */}
        <Route
          path={`${ROUTES.PARTNER.BASE}/*`}
          element={
            <ProtectedRoute allowedRoles={[UserRole.PARTNER, UserRole.ADMIN]}>
              <PartnerPortal />
            </ProtectedRoute>
          }
        />

        {/* Admin Dashboard - accessible only to admins */}
        <Route
          path={`${ROUTES.ADMIN.BASE}/*`}
          element={
            <ProtectedRoute allowedRoles={[UserRole.ADMIN]}>
              <AdminDashboard />
            </ProtectedRoute>
          }
        />

        {/* Default redirect */}
        <Route path="*" element={<Navigate to={ROUTES.HOME} replace />} />
      </Routes>
    </Suspense>
  );
}

